<div class="sav-site-container">
  <app-breadcrumb [items]="crumbs()" />
</div>

<!--
  The event banner, and only when an admin uploaded one.

  The fallback here was the same hotlinked ZAFUL swimwear advert the category page carried: another
  retailer's advertising, served from Amazon's CDN, on every event with no banner of its own.

  Dropping it is not simply a matter of deleting the tag, because the page's H1 lives in the overlay
  ON the banner, and that overlay is white text positioned absolutely against the photograph. With
  no image the surface collapses to nothing and white sits on a near-white tint — so the heading
  gets its own plain block below instead, and stays readable.
-->
@if (heroBannerUrl()) {
  <section class="sav-store-banner" aria-label="Event banner">
    <div class="sav-store-banner-surface">
      <img [src]="heroBannerUrl()" [alt]="heroAltText()" class="sav-store-banner-image" />

      <div class="sav-store-banner-overlay">
        <div class="sav-store-banner-category">
          <div class="sav-store-banner-logo" aria-label="Category icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M5 7.5A2.5 2.5 0 0 1 7.5 5h9A2.5 2.5 0 0 1 19 7.5v9a2.5 2.5 0 0 1-2.5 2.5h-9A2.5 2.5 0 0 1 5 16.5v-9Z"
                stroke="currentColor"
                stroke-width="1.5"
              />
              <path d="M8 10h8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              <path d="M8 13h5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
              <circle cx="16.5" cy="16.5" r="1.5" fill="currentColor" />
            </svg>
          </div>

          <div class="sav-store-banner-category-copy">
            <p>Deals Event</p>
            <h1 class="sav-store-banner-category-name">{{ eventName() }}</h1>
          </div>
        </div>
      </div>
    </div>
  </section>
} @else {
  <!-- Same heading, on the page rather than on a photograph. -->
  <section class="sav-event-heading" aria-label="Event heading">
    <div class="sav-site-container">
      <p>Deals Event</p>
      <h1>{{ eventName() }}</h1>
    </div>
  </section>
}

<!-- Editorial images: admin-managed lookbook when configured, else the static collection grid. -->
@if (editorial(); as ed) {
  <section class="sav-collection-grid" aria-label="Editorial collection">
    <div class="sav-site-container">
      @if (ed.fullHeading || ed.collageHeading) {
        <header class="sav-collection-grid-head">
          <div>
            <h2>{{ ed.fullHeading || ed.collageHeading }}</h2>
          </div>
        </header>
      }
      <div class="sav-collection-grid-layout">
        @if (ed.full) {
          <article class="sav-collection-grid-hero">
            <img
              [src]="ed.full.imageUrl"
              [alt]="eventName() + ' collection'"
              class="sav-collection-grid-image"
            />
            <div class="sav-collection-grid-content sav-collection-grid-content-hero">
              <h2>{{ ed.fullHeading || eventName() }}</h2>
              @if (ed.full.url) {
                <a [href]="ed.full.url" target="_blank" rel="nofollow sponsored noopener"
                  >Shop Now</a
                >
              }
            </div>
          </article>
        }
        <div class="sav-collection-grid-stack">
          @if (ed.half) {
            <article class="sav-collection-grid-tile">
              <img [src]="ed.half.imageUrl" [alt]="eventName()" class="sav-collection-grid-image" />
              <div class="sav-collection-grid-content">
                @if (ed.half.url) {
                  <a [href]="ed.half.url" target="_blank" rel="nofollow sponsored noopener"
                    >Shop Now</a
                  >
                }
              </div>
            </article>
          }
          @for (tile of ed.collage; track tile.imageUrl) {
            <article class="sav-collection-grid-tile">
              <img [src]="tile.imageUrl" [alt]="eventName()" class="sav-collection-grid-image" />
              <div class="sav-collection-grid-content">
                @if (tile.url) {
                  <a [href]="tile.url" target="_blank" rel="nofollow sponsored noopener"
                    >Shop Now</a
                  >
                }
              </div>
            </article>
          }
        </div>
      </div>
    </div>
  </section>
}

<!-- A stack of product strips, mirroring the deals-store page: recommended discounts, new
     arrivals, then one section per store taking part in this event. Each pages independently.
     A section only renders once it actually has products — an empty one is hidden rather than
     showing an empty heading. Sections that haven't loaded yet still emit the small placeholder
     below, because that is the element the lazy-load observer watches. -->
@for (section of sections(); track section.id) {
  @if (section.products.length > 0) {
    <section class="sav-essentials" [attr.aria-label]="section.heading">
      <div class="sav-site-container">
        <header class="sav-essentials-head">
          <div>
            <h2>{{ section.heading }}</h2>
            <p>Showing {{ section.products.length }} of {{ section.total }} products</p>
          </div>
        </header>

        <div class="sav-product-grid">
          @for (product of section.products; track product.title + $index) {
            <app-product-card
              [title]="product.title"
              [image]="product.image"
              [storeName]="product.storeName"
              [storeLogoUrl]="product.storeLogoUrl"
              [price]="product.price"
              [oldPrice]="product.oldPrice"
              [external]="product.isExternal"
              [detailHref]="product.detailPath"
              [productId]="product.id"
            />
          }
        </div>

        @if (hasMore(section)) {
          <div class="sav-essentials-more">
            <button type="button" (click)="loadMoreProducts(section)" [disabled]="section.loading">
              {{ section.loading ? 'Loading…' : 'Load more products' }}
            </button>
          </div>
        }
      </div>
    </section>
  } @else if (!section.loaded) {
    <div
      class="sav-essentials-placeholder"
      aria-hidden="true"
      [attr.data-lazy-section]="!section.eager ? section.id : null"
    ></div>
  }
}

<!--
  FAQs, admin-managed only. The fallback here was four hardcoded ZAFUL questions, shown on every
  event that had none of its own — the same block the category page carried.
-->
@if (faqs().length) {
  <section class="sav-faq" aria-label="Frequently asked questions">
    <div class="sav-site-container">
      <header class="sav-faq-head">
        <h2>{{ eventName() }} FAQs</h2>
        <p>Everything shoppers ask before using these offers.</p>
      </header>
      <div class="sav-store-faq-list">
        @for (faq of faqs(); track faq.question; let i = $index) {
          <details class="sav-store-faq-item" [open]="i === 0">
            <summary>{{ faq.question }}</summary>
            <!-- Admin FAQ answers are rich text; interpolating them printed the markup as text. -->
            <div class="sav-store-faq-answer" [innerHTML]="faq.answer | trustedCmsHtml"></div>
          </details>
        }
      </div>
    </div>
  </section>
}

<!-- Logo strip for the stores taking part in this event, each linking to its DEALS page (not its
     coupon page) — that is the whole point of this page.
     Hidden when none of them have a logo, rather than showing placeholder brands. -->
@if (topStores().length > 0) {
  <section class="sav-top-stores-section" aria-label="Top stores in this event">
    <div class="sav-top-stores-wrap">
      <h2>{{ eventName() }} Top Stores</h2>
      <p>The retailers carrying the most products in this category.</p>

      <div class="sav-top-store-list" appAutoScrollRow>
        @for (store of topStores(); track store.path) {
          <!-- A store with no collection has no products page to link to now that `/deals/<store>`
               is retired, so it is left out rather than linked to a 410. -->
          @if (store.path) {
            <a [routerLink]="store.path" [attr.aria-label]="store.label" class="sav-top-store">
              <app-store-logo
                class="sav-top-store-logo"
                [logoUrl]="store.logoUrl"
                [name]="store.label"
                decorative
              />
            </a>
          }
        }
      </div>
    </div>
  </section>
}

<!-- Real stores carrying this category, each linking to its deals page. Hidden entirely when the
     category has none, rather than showing placeholder brands that sell nothing in it. -->
@if (hasBrands()) {
  <section class="sav-brand-links-section" aria-label="Popular stores">
    <div class="sav-site-container">
      <header class="sav-brand-links-section-head">
        <div>
          <h2>More {{ eventName() }} Brands to Explore</h2>
          <p>Stores with products in this category</p>
        </div>
      </header>

      <div class="sav-brand-links-grid">
        @for (column of brandColumns(); track $index) {
          <div class="sav-brand-links-column">
            @for (brand of column; track brand.path) {
              <a [routerLink]="brand.path">{{ brand.label }}</a>
            }
          </div>
        }
      </div>
    </div>
  </section>
}

<!--
  Long-form guide, admin-managed only. The fallback here was a hardcoded ZAFUL handbook.
-->
@if (longContent()) {
  <section class="sav-longform" aria-label="Category savings guide">
    <div class="sav-site-container">
      <header class="sav-longform-head">
        <h2>{{ longHeading() || eventName() + ' Savings Guide' }}</h2>
        @if (longIntro()) {
          <p>{{ longIntro() }}</p>
        }
      </header>
      <div
        class="sav-longform-preview sav-longform-cms"
        [innerHTML]="longContent() | trustedCmsHtml"
      ></div>
    </div>
  </section>
}
